import Modal from 'components/common/Modal';
import Price from 'components/common/Price';
import axios from 'helpers/axios';
import config from 'helpers/config';
import dateFormats from 'helpers/dateFormats';
import DateRangeQueryModel from 'models/common/DateRangeQueryModel';
import OrderByQueryModel from 'models/common/OrderByQueryModel';
import SearchQueryParamsModel from 'models/common/SearchQueryParamsModel';
import RepairOrder from 'models/RepairOrderModel';
import SearchResults from 'models/SearchResultsModel';
import DateRangeType from 'models/types/DateRangeTypes';
import Vehicle from 'models/VehicleModel';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import textConstants from 'helpers/textConstants';
import { Link, useParams } from 'react-router-dom';
import InspectionViewModal from '../settings/menus/users/InspectionViewModal';

import {
    X,
    ChevronUp,
    ChevronDown,
    CheckCircle,
    Clock,
    Calendar,
    ArrowRight,
    FileText,
    Wrench,
    Clipboard,
    Info,
} from 'lucide-react';
import getFullVehicleName from 'helpers/vehicleFullNameHelper';

interface Props {
    vehicle: Vehicle;
    modalClosed: () => void;
}

const VehicleQuickHistoryModal: React.FC<Props> = ({ vehicle, modalClosed }: Props) => {
    const [isLoading, setIsLoading] = useState(false);
    const [repairOrders, setrepairOrders] = useState<RepairOrder[]>([]);
    const [expandedIndexes, setExpandedIndexes] = useState<Set<number>>(new Set());
    const [selectedInspectionId, setSelectedInspectionId] = useState<string | null>(null);

    const repairOrderId = useParams<{ repairOrderId: string }>().repairOrderId;

    useEffect(() => {
        getRepairOrders();
    }, []);

    const toggleExpand = (index: number) => {
        setExpandedIndexes((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(index)) {
                newSet.delete(index);
            } else {
                newSet.add(index);
            }
            return newSet;
        });
    };

    const getRepairOrders = () => {
        const queryParams: SearchQueryParamsModel & OrderByQueryModel & DateRangeQueryModel = {
            orderBy: 'createdAt',
            itemsPerPage: 100,
            page: 1,
            dateFrom: null,
            dateTo: null,
            dateRange: DateRangeType.AllTime,
            vehicleId: vehicle.id,
            includeInspections: true,
        };

        axios.get<SearchResults<RepairOrder>>(`${config.apiUrl}/repairorder`, { params: queryParams }).then((res) => {
            setrepairOrders(res.data.rows);
            setIsLoading(false);
        });
    };

    return (
        <Modal
            headerText={`Service History for ${vehicle?.year} ${vehicle?.make} ${vehicle?.model}`}
            isOpen={true}
            onClose={modalClosed}
            isLoading={isLoading}
            isBackgroundWhite={true}
            isDeleteHidden={true}
            saveText={'Close'}
            customHeader={
                <div className="bg-blue-600 text-white p-2 flex items-center justify-between w-full">
                    <div>
                        <h2 className="text-xl font-bold">Service History</h2>
                        <div className="flex  mt-1 items-baseline">
                            <span className="font-medium">{getFullVehicleName(vehicle)}</span>
                            <span className="mx-2 text-blue-200">•</span>
                            <span className="text-blue-100 text-sm">VIN: {vehicle.VIN ? vehicle.VIN : 'N/A'}</span>
                        </div>
                    </div>
                    <button className="text-white hover:bg-blue-700 p-1 rounded" onClick={modalClosed}>
                        <X size={24} />
                    </button>
                </div>
            }
        >
            <div>
                {repairOrders.length === 0 || (repairOrders.length === 1 && repairOrders[0].id === repairOrderId) ? (
                    <div className="text-center text-gray-600 font-semibold">
                        {textConstants.NO_SERVICE_HISTORY_FOUND}
                    </div>
                ) : null}
                {repairOrders.map((RO, index) => {
                    return RO.id === repairOrderId ? null : (
                        <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden" key={index}>
                            <div
                                className="bg-gray-50 p-3 flex items-center justify-between cursor-pointer"
                                onClick={() => toggleExpand(index)}
                            >
                                <div className="flex items-center space-x-3">
                                    <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded font-medium flex items-center">
                                        <FileText size={16} className="mr-1" />
                                        <span>RO# {RO.repairOrderNumber}</span>
                                    </div>
                                    {RO.title ? (
                                        <span className="font-medium">{RO.title}</span>
                                    ) : (
                                        <span className="font-medium text-gray-500">No title available</span>
                                    )}
                                </div>
                                <div className="flex items-center">
                                    <div className="flex items-center mr-4 text-gray-500">
                                        <Calendar size={16} className="mr-1" />
                                        <span>{moment(RO.createdAt).format(dateFormats.USA_DATE)}</span>
                                    </div>
                                    <button className="text-blue-600">
                                        {expandedIndexes.has(index) ? (
                                            <ChevronUp size={20} />
                                        ) : (
                                            <ChevronDown size={20} />
                                        )}
                                    </button>
                                </div>
                            </div>

                            {expandedIndexes.has(index) && (
                                <div className="p-4 bg-white">
                                    <div className="mb-4">
                                        {RO.services?.length > 0 ? (
                                            <>
                                                <h3 className="font-medium text-gray-700 mb-2 flex items-center">
                                                    <Wrench size={16} className="mr-2" />
                                                    Services
                                                </h3>
                                                <div className="space-y-2">
                                                    {RO.services.map((service, serviceIndex) => (
                                                        <div
                                                            className="flex items-center justify-between p-2 bg-gray-50 rounded"
                                                            key={serviceIndex}
                                                        >
                                                            <span>{service.title}</span>
                                                            <div className="flex items-center">
                                                                <span className="font-medium text-gray-800">
                                                                    <Price
                                                                        price={service.grandTotalCents || 0}
                                                                        isInCents={true}
                                                                    />
                                                                </span>
                                                                <div className="w-4 h-4 bg-gray-300 rounded-full ml-2"></div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        ) : (
                                            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-3 rounded flex items-start">
                                                <Info size={18} className="mr-2 mt-0.5 flex-shrink-0" />
                                                <p>
                                                    No services found for this repair order. Go to repair order page for
                                                    more details.
                                                </p>
                                            </div>
                                        )}
                                    </div>

                                    {RO.inspections?.length > 0 ? (
                                        <>
                                            <h3 className="font-medium text-gray-700 mb-2 flex items-center">
                                                <Clipboard size={16} className="mr-2" />
                                                Inspections
                                            </h3>
                                            <ul className="space-y-2 ">
                                                {RO.inspections.map((inspection, inspectionIndex) => (
                                                    <li
                                                        key={inspectionIndex}
                                                        className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                                                    >
                                                        <div className="flex items-center space-x-2">
                                                            <span className="">{inspection.name}</span>

                                                            {inspection.isCompleted ? (
                                                                <span className="ml-2 flex items-center text-green-600 text-sm">
                                                                    <CheckCircle size={14} className="mr-1" />
                                                                    Completed
                                                                </span>
                                                            ) : (
                                                                <span className="ml-2 flex items-center text-orange-600 text-sm">
                                                                    <Clock size={14} className="mr-1" />
                                                                    In Progress
                                                                </span>
                                                            )}
                                                        </div>

                                                        <button
                                                            className="text-blue-600 text-sm hover:underline font-medium"
                                                            onClick={() => setSelectedInspectionId(inspection.id)}
                                                        >
                                                            View Details
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>

                                            {selectedInspectionId && (
                                                <InspectionViewModal
                                                    inspectionId={selectedInspectionId}
                                                    onClose={() => setSelectedInspectionId(null)}
                                                />
                                            )}
                                        </>
                                    ) : null}

                                    <div className="mt-4 flex justify-end">
                                        <Link
                                            to={`/repairOrder/${RO.id}`}
                                            className="text-blue-500 hover:underline text-nowrap"
                                            target="_blank"
                                        >
                                            <button className="text-blue-600 font-medium flex items-center text-sm hover:underline">
                                                <span>View full repair order</span>
                                                <ArrowRight size={16} className="ml-1" />
                                            </button>
                                        </Link>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </Modal>
    );
};

export default VehicleQuickHistoryModal;
